import { useEffect, useRef, useState, type KeyboardEvent } from "react";

// Signed-in user dropdown at the right of the brand bar: the same control on assets.novara.com,
// sites.novara.com and askhr.novara.com. Shows who's signed in, then Settings, any app links
// (Ask HR: About Ask HR), and Sign out.
export interface Account { name: string; email: string }
export interface MenuLink { label: string; href: string; active?: boolean; onSelect: () => void }

const initials = (name: string) => name.split(/\s+/).filter(Boolean).map(w => w[0]).slice(0, 2).join("").toUpperCase() || "?";

export function UserMenu({ account, settingsHref, settingsActive, onSettings, onSignOut, links = [], defaultOpen = false }: {
  account: Account | null;
  settingsHref: string;
  settingsActive: boolean;
  onSettings: () => void;
  onSignOut: () => void;
  links?: MenuLink[];
  defaultOpen?: boolean; // tests
}) {
  const [open, setOpen] = useState(defaultOpen);
  const root = useRef<HTMLDivElement>(null), btn = useRef<HTMLButtonElement>(null), list = useRef<HTMLDivElement>(null);
  const focusAt = useRef<"first" | "last">("first");
  const items = () => [...(list.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];

  useEffect(() => {
    if (!open) return;
    const it = items(); (focusAt.current === "last" ? it[it.length - 1] : it[0])?.focus();
    const away = (e: PointerEvent) => { if (!root.current?.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("pointerdown", away);
    return () => document.removeEventListener("pointerdown", away);
  }, [open]);

  const show = (at: "first" | "last") => { focusAt.current = at; setOpen(true); };
  const close = (refocus: boolean) => { setOpen(false); if (refocus) btn.current?.focus(); };
  const onBtnKey = (e: KeyboardEvent) => { if (e.key === "ArrowDown" || e.key === "ArrowUp") { e.preventDefault(); show(e.key === "ArrowUp" ? "last" : "first"); } };
  const onListKey = (e: KeyboardEvent) => {
    const it = items(), i = it.indexOf(document.activeElement as HTMLElement);
    const go = (n: number) => { e.preventDefault(); it[(n + it.length) % it.length]?.focus(); };
    if (e.key === "ArrowDown") go(i + 1);
    else if (e.key === "ArrowUp") go(i - 1);
    else if (e.key === "Home") go(0);
    else if (e.key === "End") go(it.length - 1);
    else if (e.key === "Escape") { e.preventDefault(); close(true); }
    else if (e.key === "Tab") close(false);
  };

  const name = account?.name ?? "Account";
  return (
    <div className="usermenu" ref={root}>
      <button ref={btn} className="userbtn" type="button" aria-haspopup="menu" aria-expanded={open} aria-controls="userMenuList"
        aria-label={account ? `Account menu, signed in as ${account.name}` : "Account menu"}
        onClick={() => (open ? close(false) : show("first"))} onKeyDown={onBtnKey}>
        <span className="av" aria-hidden="true">{account ? initials(account.name) : "?"}</span>
        <span className="nm">{name.split(/\s+/)[0] || name}</span>
        <span className="caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="umenu" id="userMenuList" role="menu" aria-label="Account" ref={list} onKeyDown={onListKey}>
          <div className="who" role="presentation"><b>{account?.name ?? "Signed in"}</b>{account && account.email !== account.name && <span>{account.email}</span>}</div>
          <a role="menuitem" href={settingsHref} tabIndex={-1} aria-current={settingsActive ? "page" : undefined}
            onClick={e => { e.preventDefault(); close(true); onSettings(); }}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" /></svg>
            Settings
          </a>
          {links.map(l => (
            <a key={l.href} role="menuitem" href={l.href} tabIndex={-1} aria-current={l.active ? "page" : undefined}
              onClick={e => { e.preventDefault(); close(true); l.onSelect(); }}>
              <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9" /><path d="M12 11v6" /><path d="M12 7.5v.5" /></svg>
              {l.label}
            </a>
          ))}
          <hr role="separator" />
          <a role="menuitem" href="#" tabIndex={-1} onClick={e => { e.preventDefault(); close(false); onSignOut(); }}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" /><path d="M16 17l5-5-5-5" /><path d="M21 12H9" /></svg>
            Sign out
          </a>
        </div>
      )}
    </div>
  );
}
